import { toast } from "sonner";
import { blocksApi } from "@/lib/api";
import { EMPTY_FILTERS } from "@/lib/scheduleUtils";

// Builds the dispatcher used by the AI Command Palette. Returns an async
// function that takes a single action dict produced by the backend and
// applies it to local state via the supplied setters/handlers.
export function useAiActionDispatcher({
  data,
  patchBlock,
  patchTask,
  scheduleFloating,
  refresh,
  setBulkAction,
  setPaletteOpen,
  setFilters,
  setDetailTarget,
  setDetailOpen,
}) {
  return async function dispatchAiAction(action) {
    if (!action || !action.action) return false;
    switch (action.action) {
      case "move_block": {
        const b = data.schedule_blocks.find((x) => x.id === action.block_id);
        if (!b || !action.delta_min) return false;
        const newStart = new Date(new Date(b.start_at).getTime() + action.delta_min * 60000).toISOString().replace(/\.\d+Z$/, "Z");
        const newEnd = new Date(new Date(b.end_at).getTime() + action.delta_min * 60000).toISOString().replace(/\.\d+Z$/, "Z");
        await patchBlock(action.block_id, { start_at: newStart, end_at: newEnd });
        toast.success(`Moved by ${action.delta_min > 0 ? "+" : ""}${action.delta_min}m`);
        return true;
      }
      case "set_status": {
        const b = data.schedule_blocks.find((x) => x.id === action.block_id);
        if (!b) return false;
        await patchTask(b.task_id, { status: action.status });
        toast.success(`Status → ${action.status}`);
        return true;
      }
      case "lock_block": {
        await patchBlock(action.block_id, { is_locked: !!action.locked });
        toast.success(action.locked ? "Locked" : "Unlocked");
        return true;
      }
      case "delete_block": {
        await blocksApi.remove(action.block_id);
        await refresh();
        toast.success("Block deleted");
        return true;
      }
      case "schedule_floating": {
        const anchor = data.schedule_blocks.find((x) => x.id === action.after_block_id);
        if (!anchor) return false;
        await scheduleFloating(action.task_id, anchor);
        return true;
      }
      case "open_task": {
        const t = data.tasks.find((x) => x.id === action.task_id);
        const b = data.schedule_blocks.find((x) => x.task_id === action.task_id);
        if (!t) return false;
        setDetailTarget({ task: t, block: b });
        setDetailOpen(true);
        return true;
      }
      case "filter_room": {
        setFilters((f) => ({ ...f, rooms: action.room_ids || [] }));
        return true;
      }
      case "filter_status": {
        setFilters((f) => ({ ...f, statuses: action.statuses || [] }));
        return true;
      }
      case "clear_filters": {
        setFilters(EMPTY_FILTERS);
        toast.success("Filters cleared");
        return true;
      }
      case "bulk_add_rooms":
      case "bulk_add_crew":
      case "bulk_add_equipment":
      case "bulk_add_tasks":
      case "add_task_with_subtasks": {
        setBulkAction(action);
        setPaletteOpen(false); // hide palette while reviewing
        return true;
      }
      case "explain":
      case "unsupported":
        return true; // shown inline in the palette
      default:
        return false;
    }
  };
}
